import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  dataGridColorRowClass,
  dataGridRowHoverClass,
  dataGridSelectedRowClass,
  dataGridWhiteRowClass,
} from '@shared/styles';
import { gridExpandedSortedRowIdsSelector, gridRowNodeSelector } from '@mui/x-data-grid';
import type { GridRowClassNameParams, GridRowId, GridValidRowModel } from '@mui/x-data-grid';
import type { GridApiPremium } from '@mui/x-data-grid-premium';

type GroupId = number | string;
type GridApiRef = RefObject<GridApiPremium | null>;

const noRowIds: GridRowId[] = [];

interface GroupedRowClassNameOptions<R extends GridValidRowModel> {
  apiRef: GridApiRef;
  /** Rows sharing this value form a group and get the same color. */
  getGroupId: (row: R) => GroupId;
  /** Value identifying the selected row(s), compared against `selectedId`. */
  getSelectionId?: (row: R) => GroupId | undefined;
  selectedId?: GroupId;
}

/**
 * Color parity (0/1) for each row, in displayed order: starts at 0 and flips
 * every time the group changes, so a whole group shares one color and adjacent
 * groups differ.
 */
export function groupColorParities(orderedGroupIds: GroupId[]): number[] {
  let parity = 0;
  return orderedGroupIds.map((groupId, index) => {
    if (index > 0 && groupId !== orderedGroupIds[index - 1]) {
      parity ^= 1;
    }
    return parity;
  });
}

export function rowColorParities(
  orderedRowIds: GridRowId[],
  getGroupId: (rowId: GridRowId) => GroupId
): Map<GridRowId, number> {
  const parities = groupColorParities(orderedRowIds.map((rowId) => getGroupId(rowId)));
  return new Map(orderedRowIds.map((rowId, index) => [rowId, parities[index]]));
}

function displayedRowIds(apiRef: GridApiRef): GridRowId[] {
  return apiRef.current ? gridExpandedSortedRowIdsSelector(apiRef) : noRowIds;
}

function useRerenderOnDisplayedOrderChange(apiRef: GridApiRef) {
  const [, setRowIds] = useState<GridRowId[]>(noRowIds);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return undefined;

    const syncRowIds = () => setRowIds(displayedRowIds(apiRef));
    syncRowIds();

    return api.store.subscribe(syncRowIds);
  }, [apiRef]);
}

function useRowColorParity(apiRef: GridApiRef, getGroupId: (rowId: GridRowId) => GroupId) {
  useRerenderOnDisplayedOrderChange(apiRef);

  const getGroupIdRef = useRef(getGroupId);
  getGroupIdRef.current = getGroupId;

  const cacheRef = useRef<{ rowIds: GridRowId[]; parityByRowId: Map<GridRowId, number> }>();

  return useCallback(
    (rowId: GridRowId): number | undefined => {
      const rowIds = displayedRowIds(apiRef);
      if (cacheRef.current?.rowIds !== rowIds) {
        cacheRef.current = {
          rowIds,
          parityByRowId: rowColorParities(rowIds, (id) => getGroupIdRef.current(id)),
        };
      }
      return cacheRef.current.parityByRowId.get(rowId);
    },
    [apiRef]
  );
}

/**
 * Returns a `getRowClassName` that colors rows by consecutive groups (same
 * `getGroupId`), flipping the color each time the group changes. The order is
 * read from `apiRef`, so the colors stay aligned with the mg item number when
 * the user sorts or filters the grid — pass the same `apiRef` to the DataGrid.
 */
export function useGroupedRowClassName<R extends GridValidRowModel>({
  apiRef,
  getGroupId,
  getSelectionId,
  selectedId,
}: GroupedRowClassNameOptions<R>) {
  const getRowParity = useRowColorParity(apiRef, (rowId) => {
    const row = apiRef.current?.getRow<R>(rowId);
    return row ? getGroupId(row) : rowId;
  });

  return useCallback(
    (params: GridRowClassNameParams<R>) => {
      // Before the grid is ready we fall back to the row's own index (striped).
      const parity = getRowParity(params.id) ?? params.indexRelativeToCurrentPage % 2;
      const isSelected = selectedId !== undefined && getSelectionId?.(params.row) === selectedId;

      return clsx(
        parity === 0 ? dataGridColorRowClass : dataGridWhiteRowClass,
        dataGridRowHoverClass,
        { [dataGridSelectedRowClass]: isSelected }
      );
    },
    [getRowParity, getSelectionId, selectedId]
  );
}

/** Walk up to the top-level (depth 0) ancestor so a group header and its children share one id. */
function topLevelGroupId(apiRef: GridApiRef, id: GridRowId): GridRowId {
  let node = gridRowNodeSelector(apiRef, id);
  if (!node) return id;
  while (node.depth > 0 && node.parent != null) {
    const parent = gridRowNodeSelector(apiRef, node.parent);
    if (!parent) break;
    node = parent;
  }
  return node.id;
}

/**
 * Returns a `getRowClassName` for a grid with row grouping: every row belonging
 * to the same top-level group (the group header and all its children) shares one
 * color, and the color flips between adjacent groups. Pass the same `apiRef` to
 * the DataGrid.
 */
export function useGroupedByTreeRowClassName<R extends GridValidRowModel>(apiRef: GridApiRef) {
  const getRowParity = useRowColorParity(apiRef, (rowId) => topLevelGroupId(apiRef, rowId));

  return useCallback(
    (params: GridRowClassNameParams<R>) => {
      const parity = getRowParity(params.id) ?? params.indexRelativeToCurrentPage % 2;

      return clsx(
        parity === 0 ? dataGridColorRowClass : dataGridWhiteRowClass,
        dataGridRowHoverClass
      );
    },
    [getRowParity]
  );
}

export function useRowColorGrouping() {
  //add to getRowClassName in DataGrid to get alternating color on rows based on idGroupings
  const getRowColorByIdGroups = useCallback(
    (
      allRowGroupingIds: number[],
      rowGroupingId: number,
      index: number,
      addHoverColor?: boolean
    ) => {
      const uniqueNumbers = new Set(allRowGroupingIds);
      const duplicates = allRowGroupingIds.filter((number) => {
        if (uniqueNumbers.has(number)) {
          uniqueNumbers.delete(number);
          return false;
        }
        return number;
      });

      const isDuplicate = duplicates.includes(rowGroupingId);
      const mgItemNumbersToThisNumber = allRowGroupingIds.slice(
        0,
        allRowGroupingIds.indexOf(rowGroupingId)
      );
      const nrOfDuplicateBefore =
        mgItemNumbersToThisNumber.length - new Set(mgItemNumbersToThisNumber).size;

      const uniqueMgItemNrIndex = isDuplicate
        ? allRowGroupingIds.indexOf(rowGroupingId) - nrOfDuplicateBefore
        : index - nrOfDuplicateBefore;

      if (uniqueMgItemNrIndex % 2 === 0) {
        return addHoverColor
          ? clsx(dataGridColorRowClass, dataGridRowHoverClass)
          : dataGridColorRowClass;
      }

      return addHoverColor
        ? clsx(dataGridWhiteRowClass, dataGridRowHoverClass)
        : dataGridWhiteRowClass;
    },
    []
  );
  //add to getRowClassName in DataGrid to get striped rows
  const getRowColorForStripedTable = useCallback((value: GridRowClassNameParams) => {
    if (value.indexRelativeToCurrentPage % 2 === 0) {
      return dataGridColorRowClass;
    }
    return dataGridWhiteRowClass;
  }, []);

  return {
    getRowColorForStripedTable,
    getRowColorByIdGroups,
  };
}
